import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import Form from '@active/react-ui/Form';
import Locale from '@active/react-utils/i18n/Locale';
import isEmpty from 'lodash/isEmpty';
import { objectFilter } from '../../../../../../../../shared/utils/commonUtils';
import pubSubInstance from '../../../../../../../../shared/utils/pubSub';
import PinpadTipAndIcons from '../../../../../common/PinpadTipAndIcons';
import CurrencyInputFormField from '../CurrencyInputFormField';
import usePinpadCheckReady from '../../../../../utils/usePinpadCheckReady';
import CreditCardInfoField from '../../../../../../staticFields/PaymentInfoFields/components/CreditCardInfoField';
import WSS from '../../../../../../../../shared/lib/wssAPI';

const Pinpad = ({ paymentMethodType, errorCallback }) => {
  const [ paymentDisplayInfo, setPaymentDisplayInfo ] = useState();
  const [ pinpadPaymentInfoList, setPinpadPaymentInfoList ] = useState([]);
  const {
    paymentMethodsConfig: {
      isSingleMode = true,
      pinpadInfo: {
        pinpadProcessor,
        pinpadIdentifier,
      } = {},
    } = {},
    widgetNameSpace,
  } = useSelector(state => state.propsData);
  const {
    entities: {
      envParams: {
        currencyCode,
      },
    },
    refundTotalAmount,
  } = useSelector(state => state.mainStateData);

  const {
    entities: {
      paymentMethods: {
        pinPadInfo: {
          clientTransactionId, identifier, orderDescriptor,
        } = {},
      },
    },
  } = useSelector(state => state.paymentMethods);

  const {
    isReady, checkAPDReady, wssProcess,
  } = usePinpadCheckReady({ errorCallback });

  const isPrefillValue = useMemo(() => isSingleMode, [ isSingleMode ]);

  const initValues = useMemo(() => ({
    pinpadAmount: isPrefillValue ? refundTotalAmount : '',
  }), [ refundTotalAmount, isPrefillValue ]);

  const processADPPayment = useCallback((commitCallback) => {
    const initParameters = 'isDebitCard=true';
    const processPaymentParameters = objectFilter({
      GenerateClientId: !clientTransactionId ? true : undefined,
      ClientTransactionID: clientTransactionId,
      PinpadDataEntryTimeout: '60',
      PinpadPINEntryTimeout: '60',
      PinpadSwipeCardTimeout: '60',
      OrderID: identifier?.toString(),
      OrderAmount: refundTotalAmount?.toString(),
      OrderDescriptor: orderDescriptor?.toString(),
    });
    const paymentRequest = WSS.getWSRequestForJSON(
      WSS.RequestMethod.EXEC,
      WSS.FunctionName.PROCESS_APD_REFUND,
      initParameters,
      processPaymentParameters,
      pinpadProcessor,
      pinpadIdentifier);
    wssProcess({
      request: paymentRequest,
      resolve: ({
        value: {
          amsResponse: {
            amstransactionID, userErrorMessage, ccexpMonth, ccexpYear,
            cardType, lastFourDigits, amount: currenAmount,
          },
          clientRequest: { clientTransactionID: clientRequestTransactionID },
        },
      }) => {
        if (userErrorMessage.length > 0 && userErrorMessage !== 'Approved') {
          commitCallback({ status: false, errorCode: 'PROCESSADPPAYMENT_FAILED' });
        } else {
          const amount = Number(currenAmount);
          const pinpadPaymentInfo = {
            paymentId: amstransactionID,
            transactionId: clientRequestTransactionID,
            amount,
            paymentMethodType,
            cardType,
            lastFourDigits,
          };
          const expirationDate = `${ccexpMonth}/${ccexpYear}`;
          const newPinpadPaymentInfoList = pinpadPaymentInfoList.concat([ pinpadPaymentInfo ]);
          setPinpadPaymentInfoList(newPinpadPaymentInfoList);
          commitCallback({ status: true, pinpadPaymentInfoList: newPinpadPaymentInfoList });
          setPaymentDisplayInfo({ cardType, lastFourDigits, expirationDate });
        }
      },
      reject: () => {
        commitCallback({ status: false, errorCode: 'PROCESSADPPAYMENT_FAILED' });
      },
    });
  }, [ clientTransactionId, refundTotalAmount, wssProcess, pinpadPaymentInfoList, paymentMethodType,
    identifier, orderDescriptor, pinpadIdentifier, pinpadProcessor ]);

  const handleSubmit = useCallback(() => {
    return new Promise((resolve) => {
      if (!isSingleMode || (isSingleMode && !isEmpty(pinpadPaymentInfoList))) { // have paid successful
        resolve({ status: true, pinpadPaymentInfoList });
        return;
      }
      checkAPDReady({ resolveCallback: processADPPayment, commitCallback: resolve });
    });
  }, [ checkAPDReady, processADPPayment, pinpadPaymentInfoList, isSingleMode ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-payment-item-submit`, handleSubmit);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-payment-item-submit`, handleSubmit);
    };
  }, [ widgetNameSpace, handleSubmit ]);

  return (
    <div className="multi-payment-pinpad">
      <PinpadTipAndIcons isReady={isReady}/>
      <div className='hr-dash-line cui-mg-t-20 cui-mg-b-20' />
      <Form
        className='pinpad-info__form'
        enableReinitialize
        initialValues={initValues}
        customizeLayout>
        {
          paymentDisplayInfo
            ? <div className="pinpad-info__text">
              <CreditCardInfoField
                cardType={paymentDisplayInfo.cardType}
                lastFourDigits={paymentDisplayInfo.lastFourDigits}/>
              <div className='text-expire-date cui-mg-l-16'>
                { paymentDisplayInfo.expirationDate }
              </div>
              <div className='text-refunded cui-mg-l-16'>
                <span className="ic-check-r" />
                <Locale id="checkout.lib.components.text.refunded" />
              </div>
            </div>
            : <div className='pinpad-label'>
              <Locale id="checkout.lib.components.text.refundAmount" />
            </div>
        }
        <div className='pinpad-amount'>
          <CurrencyInputFormField
            paymentMethodType={paymentMethodType}
            isPrefillValue={isPrefillValue}
            name='pinpadAmount'
            disabled={isPrefillValue}
            code={currencyCode}/>
        </div>
      </Form>
    </div>
  );
};

export default Pinpad;
